import { useState, useEffect, useCallback } from 'react';
import type { CoinData } from '../types/interfaces';

export function useWatchlist() {
  const [watchlist, setWatchlist] = useState<string[]>(() => {
    const saved = localStorage.getItem('cryptoscopeWatchlist');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('cryptoscopeWatchlist', JSON.stringify(watchlist));
  }, [watchlist]);

  const addToWatchlist = useCallback(async (coinId: string) => {
    // Check if payment is required (50 satoshis)
    try {
      if (window.bsvPayments && typeof window.bsvPayments.getPaymentAmount === 'function' && typeof window.bsvPayments.requestPayment === 'function') {
        try {
          // Check if already in watchlist - if so, removal is free
          if (watchlist.includes(coinId)) {
            setWatchlist(prev => prev.filter(id => id !== coinId));
            return;
          }

          // Request payment for adding (50 satoshis)
          const amount = 50; // Fixed amount for watchlist
          const paymentResult = await window.bsvPayments.requestPayment('WATCHLIST', amount);
          
          if (paymentResult) {
            setWatchlist(prev => [...prev, coinId]);
          }
        } catch {
          // Payment failed or cancelled
          return;
        }
      } else {
        // If payment system not available, allow free watchlist (for testing/development)
        setWatchlist(prev => {
          if (prev.includes(coinId)) {
            return prev.filter(id => id !== coinId);
          }
          return [...prev, coinId];
        });
      }
    } catch {
      return;
    }
  }, [watchlist]);

  const removeFromWatchlist = useCallback((coinId: string) => {
    setWatchlist(prev => prev.filter(id => id !== coinId));
  }, []);

  const isInWatchlist = useCallback((coinId: string) => {
    return watchlist.includes(coinId);
  }, [watchlist]);

  const getWatchlistCoins = useCallback((cryptoData: CoinData[]) => {
    return cryptoData.filter(coin => watchlist.includes(coin.id));
  }, [watchlist]);

  return {
    watchlist,
    addToWatchlist,
    removeFromWatchlist,
    isInWatchlist,
    getWatchlistCoins,
  };
}

